import { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import subscriptionService from '../services/subscriptionService';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const [profileRes, subRes] = await Promise.all([
          api.get('/shops/profile'),
          subscriptionService.getSubscription().catch(() => ({ data: null })),
        ]);
        setUser(profileRes.data.data);
        // API returns { success, data: { hasAccess, ... } } — payload is .data
        setSubscription(subRes.data ?? null);
      } catch (error) {
        localStorage.removeItem('token');
      }
    } else {
      setSubscription(null);
    }
    setLoading(false);
  };

  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1'
  );

  const isCoolfixLocal = isLocalhost && user?.email?.toLowerCase() === 'coolfix@gmail.com';
  const hasSubscriptionAccess = subscription?.hasAccess === true || isCoolfixLocal;

  const refreshSubscription = async () => {
    try {
      const res = await subscriptionService.getSubscription();
      setSubscription(res.data ?? null);
    } catch (_) {
      setSubscription(null);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/shops/login', { email, password });
      const { token, ...userData } = response.data.data;
      localStorage.setItem('token', token);
      setUser(userData);
      const subRes = await subscriptionService.getSubscription().catch(() => ({ data: null }));
      setSubscription(subRes.data ?? null);
      toast.success('Login successful!');
      const isCoolfix = isLocalhost && (email?.toLowerCase() === 'coolfix@gmail.com' || userData?.email?.toLowerCase() === 'coolfix@gmail.com');
      const hasAccess = subRes.data?.hasAccess === true || isCoolfix;
      navigate(hasAccess ? '/dashboard' : '/subscribe');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (shopData) => {
    try {
      const response = await api.post('/shops/register', shopData);
      const { token, ...userData } = response.data.data;
      localStorage.setItem('token', token);
      setUser(userData);
      const subRes = await subscriptionService.getSubscription().catch(() => ({ data: null }));
      setSubscription(subRes.data ?? null);
      toast.success('Registration successful!');
      const hasAccess = subRes.data?.hasAccess === true;
      navigate(hasAccess ? '/dashboard' : '/subscribe');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.info('Logged out successfully');
    navigate('/login');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/shops/profile', profileData);
      setUser(response.data.data);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const value = {
    user,
    subscription,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshSubscription,
    isAuthenticated: !!user,
    hasSubscriptionAccess: hasSubscriptionAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
